<script setup>
import { useStore } from "vuex";
import { IMaskComponent } from "vue-imask";
import { computed } from "vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";

const store = useStore();

const artworkSize = computed(() => store.getters["artwork/artworkSize"]);
const surface = computed(() => store.getters["artwork/surface"]);
const downscaleFactor = computed(() => store.getters["artwork/downscaleFactor"]);
const boundingBoxSize = computed(() => ({
  width: surface.value.boundingBox.width * downscaleFactor.value,
  height: surface.value.boundingBox.height * downscaleFactor.value
}));

const pxToInch = size => store.getters["artwork/convertPxToInches"](size);
const inchToPx = size => store.getters["artwork/convertInchesToPx"](size);

const maxInchSize = size => (surface.value.boundingBox[size] / surface.value.ppi).toFixed(2);

const handleChangeSize = (size, side) => {
  const validatedValue = parseFloat(size) || boundingBoxSize.value[side];
  const minValue = Math.max(validatedValue, 1);
  const value = Math.min(inchToPx(minValue), boundingBoxSize.value[side]);
  const ratio = value / artworkSize.value[side];
  const oppositeSide = side === "width" ? "height" : "width";
  const oppositeSize = ratio * artworkSize.value[oppositeSide];
  if (oppositeSize <= boundingBoxSize.value[oppositeSide] && pxToInch(oppositeSize) >= 1) {
    store.dispatch("artwork/updateArtworkSize", { [side]: value, [oppositeSide]: oppositeSize });
    store.commit("checkout/setCurrentPlacementAction", "size_change");
  } else if (pxToInch(oppositeSize) < 1) {
    handleChangeSize(1, oppositeSide);
  } else if (oppositeSize > boundingBoxSize.value[oppositeSide]) {
    handleChangeSize(pxToInch(boundingBoxSize.value[oppositeSide]), oppositeSide);
  }
};
</script>

<template>
  <div class="artwork-size">
    <div class="artwork-size__fields-wrapper">
      <div class="artwork-size__field-wrapper artwork-size__field-wrapper--width">
        <IMaskComponent
          :mask="'num”'"
          :lazy="false"
          :blocks="{ num: { mask: Number, radix: '.' } }"
          class="artwork-size__field"
          :value="`${pxToInch(artworkSize.width)}”`"
          data-testid="artwork-size-width"
          @change="handleChangeSize($event.target.value, 'width')"
        />
        <div class="artwork-size__field-label">Width</div>
      </div>
      <div class="artwork-size__cross-wrapper">
        <UseSvg icon="cross" />
      </div>
      <div class="artwork-size__field-wrapper artwork-size__field-wrapper--height">
        <IMaskComponent
          :mask="'num”'"
          :lazy="false"
          :blocks="{ num: { mask: Number, radix: '.' } }"
          class="artwork-size__field"
          :value="`${pxToInch(artworkSize.height)}”`"
          data-testid="artwork-size-height"
          @change="handleChangeSize($event.target.value, 'height')"
        />
        <div class="artwork-size__field-label">Height</div>
      </div>
    </div>

    <div class="artwork-size__max-size-info">
      The maximum size for artwork is {{ maxInchSize("width") }}” x {{ maxInchSize("height") }}”
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";
@import "frontend/init/jiffy/mixin";

.artwork-size {
  &__fields-wrapper {
    display: grid;
    grid-column-gap: 6px;
    grid-template-areas: "width cross height";
    grid-template-columns: 1fr 18px 1fr;

    &__field-wrapper {
      &--width {
        grid-area: width;
      }

      &--height {
        grid-area: height;
      }
    }
  }

  &__cross-wrapper {
    @include svg-wrapper;

    width: 18px;
    height: 18px;
    margin-top: 11px;
    padding: 5px;
    grid-area: cross;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $pod-gray-modern-100;
    color: $pod-gray-modern-900;
    border-radius: 50%;
  }

  &__field {
    display: block;
    width: 100%;
    height: 40px;
    color: $pod-gray-modern-900;
    border: 2px solid $pod-gray-modern-200;
    border-radius: 4px;
    text-align: center;
    font-weight: 600;
    font-size: 14px;
  }

  &__field-label {
    margin-top: 1px;
    text-align: center;
    font-size: 11px;
    line-height: 1.2;
    font-weight: 500;
    color: $pod-gray-modern-500;
  }

  &__max-size-info {
    text-align: center;
    font-size: 11px;
    color: $pod-gray-modern-500;
    font-weight: bold;
    margin-top: 10px;
    height: 17px;
  }
}
</style>
